/**
 * ClubSettingsEventsPage - Event Defaults Settings
 * Issue #172: Club Event Defaults & Templates
 *
 * Sections:
 * - Time & Duration (default start time, duration)
 * - Default Location (name, address)
 * - Default Visibility (public/members_only)
 * - Description Template
 * - Check-in Defaults (enabled, method)
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useClub, useClubEventDefaults, useUpdateClubEventDefaults, useUpdateClub } from '../../hooks/useClubs';
import { TopBar, Skeleton, DurationPresets, Button } from '../../components';
import LocationPickerModal, { type LocationPickerResult } from '../../components/LocationPickerModal';
import type { ClubEventDefaults } from '../../lib/api/clubs';
import type { CheckinMethod } from '../../lib/api/events';
import {
  Clock,
  MapPin,
  Eye,
  FileText,
  CheckCircle,
  Save,
  Map,
  Globe,
  Hash,
} from 'lucide-react';

// Check-in method options
const CHECKIN_METHODS: { value: CheckinMethod; label: string; description: string }[] = [
  { value: 'gps', label: 'GPS Location', description: 'Members check in when near the event location' },
  { value: 'secret_word', label: 'Secret Word', description: 'Members enter a word announced at the event' },
  { value: 'qr_code', label: 'QR Code', description: 'Members scan a QR code displayed at the event' },
  { value: 'manual', label: 'Manual', description: 'Leaders manually check in attendees' },
];

export default function ClubSettingsEventsPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Data fetching
  const { data: clubData, isLoading: clubLoading, error: clubError } = useClub(slug || '');
  const { data: defaultsData, isLoading: defaultsLoading } = useClubEventDefaults(slug || '');
  const club = clubData?.club;
  const membership = clubData?.membership;
  const isLeaderOrOwner = membership?.role === 'leader' || membership?.is_owner;

  // Mutations
  const updateDefaultsMutation = useUpdateClubEventDefaults(slug || '');
  const updateClubMutation = useUpdateClub(slug || '');

  // Numbering toggle + next number handler (#1509)
  const numberingEnabled = club?.numbering_enabled === 1;
  const [nextNumber, setNextNumber] = useState<string>('');
  const [nextNumberDirty, setNextNumberDirty] = useState(false);

  // React docs "adjust state on prop change" — sync next number from club data
  // unless the user is actively editing.
  const [prevClubNextNumber, setPrevClubNextNumber] = useState(club?.next_event_number);
  if (prevClubNextNumber !== club?.next_event_number) {
    setPrevClubNextNumber(club?.next_event_number);
    if (club?.next_event_number !== undefined && !nextNumberDirty) {
      setNextNumber(String(club.next_event_number));
    }
  }

  const handleToggleNumbering = async () => {
    if (!club) return;
    try {
      await updateClubMutation.mutateAsync({ numbering_enabled: !numberingEnabled });
    } catch {
      // Error displayed via mutation
    }
  };

  const handleSaveNextNumber = async () => {
    const num = parseInt(nextNumber, 10);
    if (!num || num < 1 || num > 99999) return;
    try {
      await updateClubMutation.mutateAsync({ next_event_number: num });
      setNextNumberDirty(false);
    } catch {
      // Error displayed via mutation
    }
  };

  // Form state
  const [formData, setFormData] = useState<Partial<ClubEventDefaults>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  // Handle location picker confirmation
  const handleLocationPickerConfirm = (result: LocationPickerResult) => {
    setFormData(prev => ({
      ...prev,
      default_location_name: result.name || prev.default_location_name,
      default_location_address: result.address || prev.default_location_address,
      default_location_lat: result.lat,
      default_location_lng: result.lng,
    }));
    setHasChanges(true);
    setShowLocationPicker(false);
  };

  // React docs "adjust state on prop change" — initialize form when defaults
  // data loads.
  const [prevDefaultsData, setPrevDefaultsData] = useState(defaultsData);
  if (prevDefaultsData !== defaultsData) {
    setPrevDefaultsData(defaultsData);
    if (defaultsData?.event_defaults) {
      setFormData(defaultsData.event_defaults);
    }
  }

  // Track changes
  const handleChange = <K extends keyof ClubEventDefaults>(field: K, value: ClubEventDefaults[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  // Save handler
  const handleSave = async () => {
    try {
      await updateDefaultsMutation.mutateAsync(formData);
      setHasChanges(false);
    } catch {
      // Error displayed via mutation.error state
    }
  };

  // Loading state
  const isLoading = clubLoading || defaultsLoading;
  if (isLoading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Event Defaults" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="max-w-2xl mx-auto p-4 space-y-6">
            <Skeleton className="h-32 w-full rounded-xl" />
            <Skeleton className="h-48 w-full rounded-xl" />
            <Skeleton className="h-32 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  // Error/permission state
  if (clubError || !isLeaderOrOwner || !club) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Event Defaults" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-12 text-center">
            <p className="text-text-muted">
              {clubError ? 'Unable to load settings' : 'Only club leaders can access event defaults'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar title="Event Defaults" onBack={() => navigate(-1)} />

      <div className="flex-1 overflow-y-auto pt-14 pb-6">
        <div className="max-w-2xl mx-auto p-4 space-y-6">
          {/* Info Banner */}
          <div className="rounded-lg border border-oro-border bg-oro-surface p-4">
            <p className="text-sm text-text-secondary">
              Configure default values that auto-fill when creating new events for this club.
              These can always be overridden when creating an event.
            </p>
          </div>

          {/* Error Message */}
          {updateDefaultsMutation.error && (
            <div className="rounded-lg border border-oro-error/40 bg-oro-error/10 p-4">
              <p className="text-sm text-oro-error">
                {(updateDefaultsMutation.error as Error).message || 'An error occurred'}
              </p>
            </div>
          )}

          {/* Time & Duration Section */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
              <Clock className="w-5 h-5 text-accent" />
              Time & Duration
            </h2>

            <div className="bg-oro-surface border border-oro-border rounded-2xl p-4 space-y-4">
              {/* Start Time */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Default Start Time
                </label>
                <input
                  type="time"
                  value={formData.default_start_time || ''}
                  onChange={(e) => handleChange('default_start_time', e.target.value || null)}
                  className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary focus:border-accent focus:outline-none"
                />
                <p className="text-xs text-text-muted mt-1">
                  Events will default to this time when creating new events.
                </p>
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Default Duration
                </label>
                <DurationPresets
                  value={formData.default_duration_minutes ?? undefined}
                  onSelect={(minutes) => handleChange('default_duration_minutes', minutes)}
                />
              </div>
            </div>
          </section>

          {/* Location Section */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
              <MapPin className="w-5 h-5 text-accent" />
              Default Location
            </h2>

            <div className="bg-oro-surface border border-oro-border rounded-2xl p-4 space-y-4">
              {/* Header with map picker button - inside card like EventForm */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-text-secondary">Set a default meeting location</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowLocationPicker(true)}
                >
                  <Map className="w-4 h-4 mr-1 text-accent" />
                  Choose on Map
                </Button>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Location Name
                </label>
                <input
                  type="text"
                  value={formData.default_location_name || ''}
                  onChange={(e) => handleChange('default_location_name', e.target.value || null)}
                  placeholder="e.g., Central Park Boathouse"
                  className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Location Address
                </label>
                <input
                  type="text"
                  value={formData.default_location_address || ''}
                  onChange={(e) => handleChange('default_location_address', e.target.value || null)}
                  placeholder="e.g., 72nd St & Park Dr N, New York, NY"
                  className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
                />
              </div>

              {/* Show coordinates if set */}
              {formData.default_location_lat && formData.default_location_lng && (
                <p className="text-xs text-text-muted">
                  Coordinates: {formData.default_location_lat.toFixed(6)}, {formData.default_location_lng.toFixed(6)}
                </p>
              )}
            </div>
          </section>

          {/* Visibility Section */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
              <Eye className="w-5 h-5 text-accent" />
              Default Visibility
            </h2>

            <div className="bg-oro-surface border border-oro-border rounded-2xl p-4 space-y-3">
              <p className="text-sm text-text-secondary mb-2">
                Choose the default visibility for new events.
              </p>

              {/* Public Option */}
              <label
                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                  formData.default_visibility === 'public'
                    ? 'border-accent bg-accent/5'
                    : 'border-oro-border hover:border-oro-border-hover'
                }`}
              >
                <input
                  type="radio"
                  name="visibility"
                  value="public"
                  checked={formData.default_visibility === 'public'}
                  onChange={() => handleChange('default_visibility', 'public')}
                  className="mt-1 accent-accent"
                />
                <div className="flex-1">
                  <span className="font-medium text-text-primary">Public</span>
                  <p className="text-sm text-text-secondary mt-0.5">
                    Anyone can see and RSVP to events.
                  </p>
                </div>
              </label>

              {/* Members Only Option */}
              <label
                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                  formData.default_visibility === 'members_only'
                    ? 'border-accent bg-accent/5'
                    : 'border-oro-border hover:border-oro-border-hover'
                }`}
              >
                <input
                  type="radio"
                  name="visibility"
                  value="members_only"
                  checked={formData.default_visibility === 'members_only'}
                  onChange={() => handleChange('default_visibility', 'members_only')}
                  className="mt-1 accent-accent"
                />
                <div className="flex-1">
                  <span className="font-medium text-text-primary">Members Only</span>
                  <p className="text-sm text-text-secondary mt-0.5">
                    Only club members can see and RSVP to events.
                  </p>
                </div>
              </label>

              {/* Clear Selection */}
              {formData.default_visibility && (
                <button
                  type="button"
                  onClick={() => handleChange('default_visibility', null)}
                  className="text-sm text-text-muted hover:text-text-secondary underline"
                >
                  Clear selection
                </button>
              )}
            </div>
          </section>

          {/* Virtual Event Default Section - Only for virtual/hybrid clubs (#1362) */}
          {(club.meeting_mode === 'virtual' || club.meeting_mode === 'hybrid') && (
            <section className="space-y-4">
              <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                <Globe className="w-5 h-5 text-accent" />
                Event Format
              </h2>

              <div className="bg-oro-surface border border-oro-border rounded-2xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-text-primary">Default to virtual events</p>
                    <p className="text-xs text-text-muted mt-1">
                      {club.meeting_mode === 'virtual'
                        ? 'Your club is virtual-only, so events will always be virtual.'
                        : 'When enabled, new events will default to virtual format. Leaders can still create in-person events.'}
                    </p>
                  </div>
                  {club.meeting_mode === 'hybrid' && (
                    <button
                      type="button"
                      onClick={() => handleChange('default_is_virtual', !formData.default_is_virtual)}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-oro-bg ${
                        formData.default_is_virtual ? 'bg-accent' : 'bg-oro-surface-alt'
                      }`}
                      role="switch"
                      aria-checked={formData.default_is_virtual === true}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          formData.default_is_virtual ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  )}
                  {club.meeting_mode === 'virtual' && (
                    <span className="text-sm text-text-muted">Always virtual</span>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Description Template Section */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
              <FileText className="w-5 h-5 text-accent" />
              Description Template
            </h2>

            <div className="bg-oro-surface border border-oro-border rounded-2xl p-4">
              <label className="block text-sm font-medium text-text-primary mb-2">
                Default Description
              </label>
              <textarea
                value={formData.default_description_template || ''}
                onChange={(e) => handleChange('default_description_template', e.target.value || null)}
                placeholder="Enter a default description that will be pre-filled for new events..."
                rows={4}
                className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none resize-none"
              />
              <p className="text-xs text-text-muted mt-1">
                This text will be pre-filled in the description field when creating new events.
              </p>
            </div>
          </section>

          {/* Check-in Defaults Section */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-accent" />
              Check-in Defaults
            </h2>

            <div className="bg-oro-surface border border-oro-border rounded-2xl p-4 space-y-4">
              {/* Enable Check-ins Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-text-primary">Enable check-ins by default</p>
                  <p className="text-xs text-text-muted mt-1">
                    When enabled, new events will have check-ins turned on automatically.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleChange('default_check_ins_enabled', !formData.default_check_ins_enabled)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-oro-bg ${
                    formData.default_check_ins_enabled ? 'bg-accent' : 'bg-oro-surface-alt'
                  }`}
                  role="switch"
                  aria-checked={formData.default_check_ins_enabled === true}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formData.default_check_ins_enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Check-in Method */}
              {formData.default_check_ins_enabled && (
                <div className="pt-4 border-t border-oro-border space-y-3">
                  <label className="block text-sm font-medium text-text-primary">
                    Default Check-in Method
                  </label>
                  {CHECKIN_METHODS.map((method) => (
                    <label
                      key={method.value}
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                        formData.default_checkin_method === method.value
                          ? 'border-accent bg-accent/5'
                          : 'border-oro-border hover:border-oro-border-hover'
                      }`}
                    >
                      <input
                        type="radio"
                        name="checkin_method"
                        value={method.value}
                        checked={formData.default_checkin_method === method.value}
                        onChange={() => handleChange('default_checkin_method', method.value)}
                        className="mt-1 accent-accent"
                      />
                      <div className="flex-1">
                        <span className="font-medium text-text-primary">{method.label}</span>
                        <p className="text-sm text-text-secondary mt-0.5">{method.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Event Numbering (#1509) */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
              <Hash className="w-5 h-5 text-accent" />
              Event Numbering
            </h2>

            <div className="bg-oro-surface border border-oro-border rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1 mr-4">
                  <p className="text-sm font-medium text-text-primary">Number events automatically</p>
                  <p className="text-xs text-text-muted mt-1">
                    Each new event gets the next sequential number (e.g., #96)
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleToggleNumbering}
                  disabled={updateClubMutation.isPending}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-oro-bg disabled:opacity-50 ${
                    numberingEnabled ? 'bg-accent' : 'bg-oro-surface-alt'
                  }`}
                  role="switch"
                  aria-checked={numberingEnabled}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      numberingEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Next event number — shown when numbering is enabled */}
              {numberingEnabled && (
                <div className="mt-4 pt-4 border-t border-oro-border">
                  <label htmlFor="next_event_number" className="block text-sm font-medium text-text-primary mb-1">
                    Next event number
                  </label>
                  <p className="text-xs text-text-muted mb-2">
                    The next event created will start from this number.
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="relative w-28">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">#</span>
                      <input
                        id="next_event_number"
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={99999}
                        value={nextNumber}
                        onChange={(e) => {
                          setNextNumber(e.target.value);
                          setNextNumberDirty(true);
                        }}
                        className="w-full bg-oro-surface-alt border border-oro-border rounded-lg pl-7 pr-3 py-2 text-text-primary text-sm focus:border-accent focus:outline-none"
                      />
                    </div>
                    {nextNumberDirty && (
                      <Button
                        type="button"
                        onClick={handleSaveNextNumber}
                        disabled={updateClubMutation.isPending || !nextNumber || parseInt(nextNumber, 10) < 1}
                        loading={updateClubMutation.isPending}
                        variant="secondary"
                        size="sm"
                      >
                        Save
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Save Button - Inline at bottom of form */}
          <div className="pt-2">
            <Button
              onClick={handleSave}
              disabled={!hasChanges || updateDefaultsMutation.isPending}
              variant="primary"
              className="w-full"
            >
              <Save className="w-5 h-5 mr-2" />
              {updateDefaultsMutation.isPending ? 'Saving...' : hasChanges ? 'Save Changes' : 'No Changes'}
            </Button>
          </div>
        </div>
      </div>

      {/* Location Picker Modal (#1253) */}
      <LocationPickerModal
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onConfirm={handleLocationPickerConfirm}
        initialLocation={{
          lat: formData.default_location_lat ?? undefined,
          lng: formData.default_location_lng ?? undefined,
        }}
        currentLocationName={formData.default_location_name ?? undefined}
      />
    </div>
  );
}
